import React, { useEffect, useRef, useState } from "react";
import { CurationItem } from "../Api/types";
import ContentTypeIcon from "./ContentTypeIcon";

/**
 * Format a date and time string to a human-readable format.
 * @param value - The date and time string to format.
 * @returns The formatted date and time string.
 */
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
});

/**
 * Format a date and time string to a human-readable format.
 * @param value - The date and time string to format.
 * @returns The formatted date and time string.
 */
const formatDateTime = (value?: string): string => {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "—";
    }
    return dateTimeFormatter.format(date);
};

const DRAG_ENABLE_RESET_DELAY_MS = 300;
const DOUBLE_CLICK_THRESHOLD_MS = 350;
const LIST_ID_MAX_DISPLAY_LENGTH = 20;
const LIST_ID_PREFIX_LENGTH = 10;
const LIST_ID_SUFFIX_LENGTH = 6;

/**
 * Get the timestamp details for a given item.
 * @param item
 * @Returns { label: string, value: string }
 */
const getTimestampDetails = (item: CurationItem): { label: string; value: string } => {
    const hasMeaningfulUpdate = Boolean(
        item.updateTime &&
            (item.changed === true || !item.createTime || item.updateTime !== item.createTime),
    );

    if (hasMeaningfulUpdate) {
        return {
            label: "Last edited",
            value: formatDateTime(item.updateTime),
        };
    }

    return {
        label: "Created",
        value: formatDateTime(item.createTime),
    };
};

/**
 * Resolve the child count for a given item.
 * @param item
 * @Returns { number }
 */
const resolveChildCount = (item: CurationItem): number => {
    if (typeof item.numChildren === "number") {
        return item.numChildren;
    }
    if (Array.isArray(item.children)) {
        return item.children.length;
    }
    return 0;
};

/**
 * Props for the ContentChildrenGrid component.
 * @param items - The list of items to display.
 * @param selectedItemIds - The IDs of the selected items.
 * @param draggedItemIds - The IDs of the items being dragged.
 * @param dragOverItemId - The ID of the item being dragged over.
 * @param dragEnabled - Whether dragging is enabled.
 * @param isSmartList - Whether the list is a smart list.
 * @param onItemClick - Callback function for when an item is clicked.
 * @param onItemDoubleClick - Callback function for when an item is double-clicked.
 * @param onSelectionDotKeyDown - Callback function for when a selection dot is pressed.
 */
type ContentChildrenGridProps = {
    items: CurationItem[];
    selectedItemIds: number[];
    draggedItemIds: number[];
    dragOverItemId: number | null;
    dragEnabled: boolean;
    isSmartList: boolean;
    onItemClick: (itemId: number, allowMultiSelect: boolean) => void;
    onItemDoubleClick: (item: CurationItem) => void;
    onSelectionDotKeyDown: (event: React.KeyboardEvent<HTMLDivElement>, itemId: number) => void;
    onDragStart: (event: React.DragEvent<HTMLDivElement>, item: CurationItem) => void;
    onDragEnd: () => void;
    onDragOver: (event: React.DragEvent<HTMLDivElement>, item: CurationItem) => void;
    onDragLeave: (item: CurationItem) => void;
    onDrop: (event: React.DragEvent<HTMLDivElement>, item: CurationItem) => void;
    isDroppableItem: (item: CurationItem | null | undefined) => boolean;
};

/**
 * Component to display a grid of content children.
 * @param items
 * @param selectedItemIds
 * @param draggedItemIds
 * @param dragOverItemId
 * @param dragEnabled
 * @param isSmartList
 * @param onItemClick
 * @param onItemDoubleClick
 * @param onSelectionDotKeyDown
 * @param onDragStart
 * @param onDragEnd
 * @param onDragOver
 * @param onDragLeave
 * @param onDrop
 * @param isDroppableItem
 * @constructor
 */
const ContentChildrenGrid: React.FC<ContentChildrenGridProps> = ({
    items,
    selectedItemIds,
    draggedItemIds,
    dragOverItemId,
    dragEnabled,
    isSmartList,
    onItemClick,
    onItemDoubleClick,
    onSelectionDotKeyDown,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    isDroppableItem,
}) => {
    const [dragDisabledItemId, setDragDisabledItemId] = useState<number | null>(null);
    const dragDisableTimeoutRef = useRef<number | null>(null);
    const doubleHandledRef = useRef<{ id: number; timestamp: number } | null>(null);

    const scheduleDragEnableReset = () => {
        if (dragDisableTimeoutRef.current !== null) {
            window.clearTimeout(dragDisableTimeoutRef.current);
        }
        dragDisableTimeoutRef.current = window.setTimeout(() => {
            setDragDisabledItemId(null);
            dragDisableTimeoutRef.current = null;
        }, DRAG_ENABLE_RESET_DELAY_MS);
    };

    useEffect(() => {
        return () => {
            if (dragDisableTimeoutRef.current !== null) {
                window.clearTimeout(dragDisableTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!dragEnabled) {
            setDragDisabledItemId(null);
            doubleHandledRef.current = null;
        }
    }, [dragEnabled]);

    if (items.length === 0) {
        return <p className="children-empty-state">The list is empty</p>;
    }

    return (
        <div className="children-grid">
            {items.map((item) => {
                const isAsset = item.contentType?.toLowerCase() === "asset";
                return (
                    <div key={item.id} className="grid-item-wrapper">
                        <div
                            className={`child-item-square ${selectedItemIds.includes(item.id) ? "selected" : ""} ${draggedItemIds.includes(item.id) ? "dragging" : ""} ${dragOverItemId === item.id ? "drag-over" : ""} ${isDroppableItem(item) ? "droppable" : ""} ${isAsset ? "non-navigable" : ""}`}
                            onClick={(event) => {
                                const allowMultiSelect =
                                    event.shiftKey || event.metaKey || event.ctrlKey;
                                const handledDouble = doubleHandledRef.current;

                                if (
                                    !allowMultiSelect &&
                                    handledDouble &&
                                    handledDouble.id === item.id &&
                                    Date.now() - handledDouble.timestamp <=
                                        DOUBLE_CLICK_THRESHOLD_MS
                                ) {
                                    doubleHandledRef.current = null;
                                    return;
                                }

                                doubleHandledRef.current = null;
                                onItemClick(item.id, allowMultiSelect);
                            }}
                            onDoubleClick={() => {
                                setDragDisabledItemId(null);
                                onItemDoubleClick(item);
                            }}
                            onDragStart={(event) => {
                                if (!dragEnabled) {
                                    event.preventDefault();
                                    return;
                                }
                                if (dragDisabledItemId === item.id) {
                                    event.preventDefault();
                                    return;
                                }
                                onDragStart(event, item);
                            }}
                            onDragEnd={onDragEnd}
                            onDragOver={(event) => {
                                if (!dragEnabled) {
                                    return;
                                }
                                onDragOver(event, item);
                            }}
                            onDragEnter={(event) => {
                                if (!dragEnabled) {
                                    return;
                                }
                                onDragOver(event, item);
                            }}
                            onDragLeave={() => {
                                if (!dragEnabled) {
                                    return;
                                }
                                onDragLeave(item);
                            }}
                            onDrop={(event) => {
                                if (!dragEnabled) {
                                    event.preventDefault();
                                    return;
                                }
                                onDrop(event, item);
                            }}
                            onMouseDown={(event) => {
                                const allowMultiSelect =
                                    event.shiftKey || event.metaKey || event.ctrlKey;

                                if (dragEnabled && event.detail > 1) {
                                    setDragDisabledItemId(item.id);
                                    scheduleDragEnableReset();
                                }

                                if (!allowMultiSelect && event.detail === 2) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    doubleHandledRef.current = {
                                        id: item.id,
                                        timestamp: Date.now(),
                                    };
                                    setDragDisabledItemId(null);
                                    onItemDoubleClick(item);
                                }
                            }}
                            role="button"
                            aria-pressed={selectedItemIds.includes(item.id)}
                            draggable={dragEnabled}
                        >
                            {(() => {
                                const timestamp = getTimestampDetails(item);
                                const childCount = resolveChildCount(item);
                                const typeLabel = item.contentType
                                    ? item.contentType.replace(/_/g, " ").toUpperCase()
                                    : "CONTENT";
                                const statusLabel = item.published ? "Published" : "Draft";
                                const listIdentifier = item.listId || "—";

                                const displayListId =
                                    listIdentifier.length > LIST_ID_MAX_DISPLAY_LENGTH
                                        ? `${listIdentifier.slice(0, LIST_ID_PREFIX_LENGTH)}…${listIdentifier.slice(-LIST_ID_SUFFIX_LENGTH)}`
                                        : listIdentifier;

                                return (
                                    <>
                                        <div className="child-card-top">
                                            <div className="child-card-icon-shell" aria-hidden>
                                                <ContentTypeIcon
                                                    contentType={item.contentType}
                                                    item={item}
                                                />
                                            </div>
                                            <div className="child-card-main">
                                                <div className="child-card-title-row">
                                                    <span
                                                        className="child-item-title"
                                                        title={item.title || "No Title"}
                                                    >
                                                        {item.title || "No Title"}
                                                    </span>
                                                </div>
                                                <div
                                                    className="child-item-meta"
                                                    aria-label="Item metadata"
                                                >
                                                    <div className="child-card-chip">
                                                        <span className="child-card-chip-label">
                                                            {timestamp.label}
                                                        </span>
                                                        <span className="child-card-chip-value">
                                                            {timestamp.value}
                                                        </span>
                                                    </div>
                                                    <div className="child-card-chip">
                                                        <span className="child-card-chip-label">
                                                            Items
                                                        </span>
                                                        <span className="child-card-chip-value">
                                                            {childCount}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div
                                                className="child-card-side"
                                                aria-label={`Content type ${typeLabel}`}
                                            >
                                                <span className="child-card-type">{typeLabel}</span>
                                                <span
                                                    className={`child-card-publish ${item.published ? "published" : "draft"}`}
                                                >
                                                    {statusLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="child-card-footer">
                                            <span
                                                className="child-card-badge"
                                                title={`Item ID ${item.id}`}
                                            >
                                                ID #{item.id}
                                            </span>
                                            <span
                                                className="child-card-badge"
                                                title={`List ID ${listIdentifier}`}
                                            >
                                                List {displayListId}
                                            </span>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                        {!isSmartList && (
                            <div
                                className={`selection-dot ${selectedItemIds.includes(item.id) ? "selected" : ""}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onItemClick(item.id, true);
                                }}
                                onKeyDown={(event) => onSelectionDotKeyDown(event, item.id)}
                                role="button"
                                tabIndex={0}
                                aria-pressed={selectedItemIds.includes(item.id)}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ContentChildrenGrid;
